import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './CenterModalWindow.css';
import Tooltip from '../Tooltip/Tooltip';

/** Dedicated inner scrollports (flex modals) — always allow touch so vertical pan reaches them. */
const MODAL_INNER_SCROLL_PORT_SELECTORS = [
  '.support-open-disc-compose-create-modal-scroll-body',
  '.support-open-disc-edit-post-modal-scroll',
  '.support-open-disc-create-post-modal-scroll',
  '.support-open-disc-create-post-modal-scroll--edit',
  '.cnr-deep-replies-modal-scroll',
  '[data-data-studio-scroll="main"]',
  '[data-modal-scroll-port]',
  '.audio-player-session__playlist-wrap',
  '.sb-audio-player-modal__scroll',
  '.sb-text-editor-modal__body',
];

const SWIPE_CLOSE_THRESHOLD_PX = 72;
const SWIPE_CLOSE_LOCK_PX = 10;
const SWIPE_DISMISS_MS = 240;
const SWIPE_SNAP_BACK_MS = 260;

const isSwipeCloseViewport = (maxWidthPx = 960) => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

const getModalInnerScrollPort = (target, modalContent) => {
  if (!(target instanceof Element) || !(modalContent instanceof HTMLElement)) return null;
  let node = target;
  let matched = null;
  while (node && node !== modalContent) {
    if (node instanceof HTMLElement) {
      for (const selector of MODAL_INNER_SCROLL_PORT_SELECTORS) {
        if (node.matches(selector)) {
          if (window.getComputedStyle(node).display === 'contents') break;
          matched = node;
          break;
        }
      }
    }
    node = node.parentElement;
  }
  return matched;
};

const getAxisScrollablesWithin = (target, boundary) => {
  let vertical = null;
  let horizontal = null;
  let node = target instanceof Node ? target : null;
  while (node && node !== boundary) {
    if (node instanceof HTMLElement) {
      const { overflowY, overflowX } = window.getComputedStyle(node);
      if (
        !vertical &&
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        vertical = node;
      }
      if (
        !horizontal &&
        (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') &&
        node.scrollWidth > node.clientWidth
      ) {
        horizontal = node;
      }
    }
    node = node.parentElement;
  }
  return { vertical, horizontal };
};

const getScrollableWithin = (target, boundary) => {
  const { vertical, horizontal } = getAxisScrollablesWithin(target, boundary);
  return vertical || horizontal;
};

const canScrollElement = (el, deltaY) => {
  if (deltaY < 0) return el.scrollTop > 0;
  if (deltaY > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
  return false;
};

const getWheelScrollableWithin = (target, boundary, deltaY) => {
  let node = target instanceof Node ? target : null;
  while (node && node !== boundary) {
    if (node instanceof HTMLElement) {
      const { overflowY } = window.getComputedStyle(node);
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight &&
        canScrollElement(node, deltaY)
      ) {
        return node;
      }
    }
    node = node.parentElement;
  }
  return null;
};

const getEmojiPopoverRoot = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest('.cnr-emoji-popover--floating');
};

const getPortaledDropdownMenuRoot = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest(
    [
      '.reusable-dropdown-menu--portal',
      '.gb__menu--portaled',
      '.wfm-text-editor__file-dropdown-menu',
      '.wfm-text-editor__file-submenu-portal',
      '.wfm-text-editor__typography-dropdown-menu',
    ].join(', ')
  );
};

/** TextEditor mobile expand portals to body above modals — must not block its scroll. */
const getMobileExpandedEditorRoot = (target) => {
  if (!(target instanceof Element)) return null;
  return target.closest('.wfm-text-editor--mobile-expanded');
};

/** Editable fields — swipe-to-close must not steal touch gestures used for text selection. */
const TEXT_INPUT_TARGET_SELECTOR = [
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="hidden"]):not([type="image"]):not([type="range"]):not([type="color"])',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[contenteditable=""]',
  '.wfm-text-editor__surface',
].join(', ');

const isTextInputTarget = (target) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(TEXT_INPUT_TARGET_SELECTOR));
};

/** Range sliders (seek, volume, EQ) — modal touch-scroll lock must not block scrubbing. */
const isRangeSliderTarget = (target) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('input[type="range"]'));
};

const allowWheelInScrollRoot = (event, root) => {
  const scrollable = getWheelScrollableWithin(event.target, root, event.deltaY);
  if (scrollable) {
    return true;
  }
  return false;
};

/** Only the topmost open modal should receive Escape (nested modal stacks). */
const modalEscapeStack = [];
let modalEscapeListenerAttached = false;

const handleModalEscapeKey = (event) => {
  if (event.key !== 'Escape' || modalEscapeStack.length === 0) return;
  const top = modalEscapeStack[modalEscapeStack.length - 1];
  if (!top) return;
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  top.onClose();
};

const attachModalEscapeListener = () => {
  if (modalEscapeListenerAttached) return;
  window.addEventListener('keydown', handleModalEscapeKey, true);
  modalEscapeListenerAttached = true;
};

const detachModalEscapeListenerIfEmpty = () => {
  if (modalEscapeStack.length > 0 || !modalEscapeListenerAttached) return;
  window.removeEventListener('keydown', handleModalEscapeKey, true);
  modalEscapeListenerAttached = false;
};

const pushModalEscapeEntry = (entry) => {
  modalEscapeStack.push(entry);
  attachModalEscapeListener();
};

const removeModalEscapeEntry = (entry) => {
  const idx = modalEscapeStack.indexOf(entry);
  if (idx >= 0) modalEscapeStack.splice(idx, 1);
  detachModalEscapeListenerIfEmpty();
};

const clearSwipeDragStyles = (content, overlay) => {
  if (content instanceof HTMLElement) {
    content.style.transform = '';
    content.style.transition = '';
  }
  if (overlay instanceof HTMLElement) {
    overlay.classList.remove('is-swiping');
  }
};

const applySwipeDragStyles = (content, overlay, dragX) => {
  if (!(content instanceof HTMLElement)) return;
  content.style.transition = 'none';
  content.style.transform = `translate3d(${dragX}px, 0, 0)`;
  if (overlay instanceof HTMLElement) {
    overlay.classList.add('is-swiping');
  }
};

/** Minimized Storage Box audio player — overlay is click-through; page must keep scrolling. */
function isPassThroughModalOverlay(overlay) {
  return (
    overlay instanceof HTMLElement &&
    overlay.classList.contains('sb-audio-player-modal-overlay--minimized')
  );
}

const Modal = ({
  isOpen,
  onClose,
  children,
  width = '500px',
  height = '300px',
  contentClassName = '',
  overlayClassName = '',
  contentStyle,
  overlayStyle,
  useAnchoredLayout = false,
  swipeToClose = false,
  swipeToCloseMaxWidth = 960,
  closeButtonClassName = '',
  closeButtonTooltip = 'Escape to close',
  headerActions = null,
}) => {
  const overlayRef = useRef(null);
  const touchGestureRef = useRef(null);
  const swipeCloseGestureRef = useRef({ active: false, locked: false, x: 0, y: 0 });
  const swipeCloseTrackingRef = useRef(false);
  const escapeStackEntryRef = useRef(null);
  const swipeToCloseRef = useRef(swipeToClose);
  swipeToCloseRef.current = swipeToClose;

  const closeModal = useCallback(() => {
    onClose();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement) {
          active.blur();
        }
      });
    });
  }, [onClose]);

  const closeModalRef = useRef(closeModal);
  closeModalRef.current = closeModal;

  useEffect(() => {
    if (!isOpen) return undefined;

    const entry = {
      onClose: () => closeModalRef.current(),
    };
    escapeStackEntryRef.current = entry;
    pushModalEscapeEntry(entry);

    return () => {
      if (escapeStackEntryRef.current) {
        removeModalEscapeEntry(escapeStackEntryRef.current);
        escapeStackEntryRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !swipeToClose || !isSwipeCloseViewport(swipeToCloseMaxWidth)) return undefined;

    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscrollX = body.style.overscrollBehaviorX;
    const prevHtmlOverscrollX = html.style.overscrollBehaviorX;

    body.style.overflow = 'hidden';
    body.style.overscrollBehaviorX = 'none';
    html.style.overscrollBehaviorX = 'none';

    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.overscrollBehaviorX = prevBodyOverscrollX;
      html.style.overscrollBehaviorX = prevHtmlOverscrollX;
    };
  }, [isOpen, swipeToClose, swipeToCloseMaxWidth]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const isTopModal = () => {
      const entry = escapeStackEntryRef.current;
      return Boolean(entry && modalEscapeStack[modalEscapeStack.length - 1] === entry);
    };

    const canSwipeClose = () =>
      swipeToCloseRef.current && isSwipeCloseViewport(swipeToCloseMaxWidth) && isTopModal();

    const resetSwipeCloseGesture = () => {
      const overlay = overlayRef.current;
      const content = overlay?.querySelector('.modal-content');
      clearSwipeDragStyles(content, overlay);
      swipeCloseGestureRef.current = { active: false, locked: false, x: 0, y: 0 };
      swipeCloseTrackingRef.current = false;
    };

    const snapSwipeBack = () => {
      const overlay = overlayRef.current;
      const content = overlay?.querySelector('.modal-content');
      if (!(content instanceof HTMLElement)) {
        resetSwipeCloseGesture();
        return;
      }

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        resetSwipeCloseGesture();
      };

      content.style.transition = `transform ${SWIPE_SNAP_BACK_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      content.style.transform = 'translate3d(0, 0, 0)';
      content.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, SWIPE_SNAP_BACK_MS + 40);
    };

    const dismissSwipeClose = (direction) => {
      const overlay = overlayRef.current;
      const content = overlay?.querySelector('.modal-content');
      if (!(content instanceof HTMLElement)) {
        resetSwipeCloseGesture();
        closeModalRef.current();
        return;
      }

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearSwipeDragStyles(content, overlay);
        closeModalRef.current();
      };

      const offscreen = direction === 'right' ? '100%' : '-100%';
      content.style.transition = `transform ${SWIPE_DISMISS_MS}ms cubic-bezier(0.4, 0, 1, 1)`;
      content.style.transform = `translate3d(${offscreen}, 0, 0)`;
      content.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, SWIPE_DISMISS_MS + 40);
    };

    const handleWheel = (event) => {
      const overlay = overlayRef.current;
      if (isPassThroughModalOverlay(overlay)) return;

      const targetModalOverlay =
        event.target instanceof Element
          ? event.target.closest('.modal-overlay.center-modal-window__overlay')
          : null;
      if (targetModalOverlay && targetModalOverlay !== overlay) {
        return;
      }

      const emojiPopover = getEmojiPopoverRoot(event.target);

      if (emojiPopover) {
        if (allowWheelInScrollRoot(event, emojiPopover)) return;
        return;
      }

      const portaledMenu = getPortaledDropdownMenuRoot(event.target);
      if (portaledMenu) {
        if (allowWheelInScrollRoot(event, portaledMenu)) return;
        return;
      }

      const expandedEditor = getMobileExpandedEditorRoot(event.target);
      if (expandedEditor) {
        if (allowWheelInScrollRoot(event, expandedEditor)) return;
        if (expandedEditor.contains(event.target)) return;
      }

      if (event.target instanceof Element && event.target.closest('.cashflow-pinch-zoom, .mobile-media-viewer, .mobile-media-viewer-overlay')) {
        return;
      }

      if (event.target instanceof Element && event.target.closest('.support-open-disc-data-studio-overlay')) {
        const studioShell = event.target.closest('.support-open-disc-data-studio-shell');
        if (studioShell && allowWheelInScrollRoot(event, studioShell)) return;
        return;
      }

      if (!overlay?.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const modalContent = overlay.querySelector('.modal-content');
      if (modalContent?.contains(event.target)) {
        if (getWheelScrollableWithin(event.target, modalContent, event.deltaY)) {
          return;
        }
      }

      event.preventDefault();
    };

    const handleTouchMove = (event) => {
      const overlay = overlayRef.current;
      if (isPassThroughModalOverlay(overlay)) return;

      const targetModalOverlay =
        event.target instanceof Element
          ? event.target.closest('.modal-overlay.center-modal-window__overlay')
          : null;
      if (targetModalOverlay && targetModalOverlay !== overlay) {
        return;
      }

      if (getEmojiPopoverRoot(event.target)) {
        return;
      }

      const portaledMenu = getPortaledDropdownMenuRoot(event.target);
      if (portaledMenu) {
        return;
      }

      if (event.target instanceof Element && event.target.closest('.wfm-image-slider, .wfm-image-slider__stage')) {
        return;
      }

      if (event.target instanceof Element && event.target.closest('.cashflow-pinch-zoom, .mobile-media-viewer, .mobile-media-viewer-overlay')) {
        return;
      }

      if (event.target instanceof Element && event.target.closest('.support-open-disc-data-studio-overlay')) {
        return;
      }

      const expandedEditor = getMobileExpandedEditorRoot(event.target);
      if (expandedEditor) {
        return;
      }

      if (isTextInputTarget(event.target)) {
        return;
      }

      if (isRangeSliderTarget(event.target)) {
        return;
      }

      if (canSwipeClose() && overlay?.contains(event.target) && event.touches.length === 1) {
        const gesture = swipeCloseGestureRef.current;
        if (gesture.active) {
          const touch = event.touches[0];
          const dx = touch.clientX - gesture.x;
          const dy = touch.clientY - gesture.y;
          const content = overlay.querySelector('.modal-content');

          if (!gesture.locked) {
            if (Math.abs(dx) < SWIPE_CLOSE_LOCK_PX && Math.abs(dy) < SWIPE_CLOSE_LOCK_PX) {
              swipeCloseTrackingRef.current = true;
              if (event.cancelable) event.preventDefault();
              return;
            }
            if (Math.abs(dx) > Math.abs(dy)) {
              swipeCloseGestureRef.current = { ...gesture, locked: true };
              swipeCloseTrackingRef.current = true;
            } else {
              resetSwipeCloseGesture();
              return;
            }
          }

          if (swipeCloseGestureRef.current.locked) {
            applySwipeDragStyles(content, overlay, dx);
            if (event.cancelable) event.preventDefault();
            return;
          }
        }
      }

      if (!overlay?.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const modalContent = overlay.querySelector('.modal-content');
      if (modalContent?.contains(event.target)) {
        if (swipeCloseTrackingRef.current || swipeCloseGestureRef.current.locked) {
          if (event.cancelable) event.preventDefault();
          return;
        }

        if (getModalInnerScrollPort(event.target, modalContent)) {
          return;
        }

        const { vertical, horizontal } = getAxisScrollablesWithin(event.target, modalContent);
        const touch = touchGestureRef.current;
        if (touch && event.touches.length === 1) {
          const dx = Math.abs(event.touches[0].clientX - touch.x);
          const dy = Math.abs(event.touches[0].clientY - touch.y);
          if (dy > dx && vertical) return;
          if (dx > dy && horizontal) return;
        }
        if (vertical || horizontal) return;
      }

      event.preventDefault();
    };

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        touchGestureRef.current = null;
        resetSwipeCloseGesture();
        return;
      }

      if (event.target instanceof Element && event.target.closest('.support-open-disc-data-studio-overlay')) {
        touchGestureRef.current = null;
        resetSwipeCloseGesture();
        return;
      }

      touchGestureRef.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      if (
        canSwipeClose() &&
        overlayRef.current?.contains(event.target) &&
        !isTextInputTarget(event.target) &&
        !isRangeSliderTarget(event.target)
      ) {
        swipeCloseGestureRef.current = {
          active: true,
          locked: false,
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      } else {
        resetSwipeCloseGesture();
      }
    };

    const handleTouchEnd = (event) => {
      if (canSwipeClose() && swipeCloseGestureRef.current.locked) {
        const touch = event.changedTouches[0];
        if (touch) {
          const gesture = swipeCloseGestureRef.current;
          const dx = touch.clientX - gesture.x;
          const dy = touch.clientY - gesture.y;
          if (Math.abs(dx) > Math.abs(dy)) {
            if (dx <= -SWIPE_CLOSE_THRESHOLD_PX) {
              dismissSwipeClose('left');
              touchGestureRef.current = null;
              return;
            }
            if (dx >= SWIPE_CLOSE_THRESHOLD_PX) {
              dismissSwipeClose('right');
              touchGestureRef.current = null;
              return;
            }
          }
        }
        snapSwipeBack();
        touchGestureRef.current = null;
        return;
      }

      resetSwipeCloseGesture();
      touchGestureRef.current = null;
    };

    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true, capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      touchGestureRef.current = null;
      resetSwipeCloseGesture();
    };
  }, [isOpen, swipeToCloseMaxWidth]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={[
        'modal-overlay',
        'center-modal-window__overlay',
        useAnchoredLayout ? 'modal-overlay--anchored' : '',
        swipeToClose ? 'center-modal-window__overlay--swipe-close' : '',
        overlayClassName,
      ]
        .filter(Boolean)
        .join(' ')}
      style={overlayStyle}
      onClick={closeModal}
    >
      <div
        className={['modal-content', 'center-modal-window__content', contentClassName]
          .filter(Boolean)
          .join(' ')}
        style={{
          ...(useAnchoredLayout ? null : { width, height }),
          ...contentStyle,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          {headerActions ? <div className="modal-header-actions">{headerActions}</div> : null}
          <div
            className={['modal-close-icon', closeButtonClassName].filter(Boolean).join(' ')}
            onClick={closeModal}
          >
            <Tooltip text={closeButtonTooltip} position="left">
              &times;
            </Tooltip>
          </div>
        </div>
        <div className="modal-children-container">{children}</div>
        <div className="extra-space" />
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
